import { useCallback, useEffect, useState } from "react";
import type { AgencyPublicScanOpportunityV1 } from "@enrein/shared-contracts/contracts/agency-unlock";
import type { StudioApiClient } from "../../../api.js";
import { GuidedButton, GuidedFlowCopy } from "../primitives/GuidedPrimitives.js";

export function GuidedOpportunitiesView({
  api,
  selectedClaimId,
  onSelectedClaimIdChange,
  onNavigate,
}: {
  readonly api: StudioApiClient;
  readonly selectedClaimId: string | null;
  readonly onSelectedClaimIdChange: (claimId: string | null) => void;
  readonly onNavigate: (view: string) => void;
}) {
  const [opportunities, setOpportunities] = useState<AgencyPublicScanOpportunityV1[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listPublicScanOpportunities();
      setOpportunities(list.opportunities);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selected =
    opportunities.find((row) => row.claimId === selectedClaimId) ??
    (opportunities.length > 0 ? opportunities[0] : null);

  useEffect(() => {
    if (selected && selected.claimId !== selectedClaimId) {
      onSelectedClaimIdChange(selected.claimId);
    }
  }, [onSelectedClaimIdChange, selected, selectedClaimId]);

  async function claimReportRef(publicReportRef: string) {
    setMessage(null);
    setError(null);
    try {
      const result = await api.claimPublicReport(publicReportRef);
      setMessage(
        result.idempotentReplay
          ? `Report ${result.publicReportRef} was already claimed.`
          : `Claimed report ${result.publicReportRef}.`,
      );
      onSelectedClaimIdChange(result.opportunity.claimId);
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }

  return (
    <section className="enrein-guided-view is-active" id="view-scan-opportunities" aria-label="Scan opportunities">
      <div className="enrein-guided-identity-strip">
        <GuidedButton variant="subtle" onClick={() => onNavigate("home")}>
          ← Home
        </GuidedButton>
        <GuidedButton variant="subtle" onClick={() => onNavigate("scan")}>
          New scan
        </GuidedButton>
      </div>

      <GuidedFlowCopy title="Public scan opportunities" kicker="Existing client revenue">
        <p className="enrein-guided-muted">
          Ranked opportunities from claimed public scans — generate Fix Kit or relaunch preview per claim.
        </p>
      </GuidedFlowCopy>

      {message ? <p className="enrein-guided-muted">{message}</p> : null}
      {error ? <p className="enrein-guided-validation">{error}</p> : null}

      <div className="enrein-guided-embedded-panel">
        <div className="enrein-guided-btn-row">
          <GuidedButton variant="line" onClick={() => void refresh()}>
            Refresh list
          </GuidedButton>
        </div>
        {loading && opportunities.length === 0 ? <p className="enrein-guided-muted">Loading opportunities…</p> : null}
        {!loading && opportunities.length === 0 ? (
          <p className="enrein-guided-muted">No claimed reports yet. Run a scan from Home first.</p>
        ) : (
          <ul className="enrein-guided-opportunity-list">
            {opportunities.map((row) => (
              <li key={row.claimId}>
                <button
                  type="button"
                  className={
                    selected?.claimId === row.claimId
                      ? "enrein-guided-opportunity-item is-active"
                      : "enrein-guided-opportunity-item"
                  }
                  onClick={() => onSelectedClaimIdChange(row.claimId)}
                >
                  <span>{row.publicReportRef}</span>
                  <span className="enrein-guided-chip">{row.scoreLabel}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected ? (
        <div className="enrein-guided-opportunity-detail">
          <GuidedFlowCopy title={selected.publicReportRef} kicker="Opportunity">
            <dl className="enrein-guided-handoff-meta">
              <div>
                <dt>Score</dt>
                <dd>
                  {selected.score} — {selected.scoreLabel}
                </dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{selected.opportunityStatus}</dd>
              </div>
              <div>
                <dt>Next action</dt>
                <dd>{selected.nextAction}</dd>
              </div>
            </dl>
          </GuidedFlowCopy>

          <div className="enrein-guided-embedded-panel enrein-guided-embedded-panel--nested">
            <h4 className="enrein-guided-kicker">Fix Kit &amp; Relaunch preview</h4>
            <p className="enrein-guided-muted">
              Guided rollback shell — use V10 Studio → Scans / Proof for Fix Kit, relaunch preview, and batch audit.
            </p>
          </div>
        </div>
      ) : null}

      <ClaimReportInline api={api} onClaim={(ref) => void claimReportRef(ref)} />
    </section>
  );
}

function ClaimReportInline({
  api: _api,
  onClaim,
}: {
  readonly api: StudioApiClient;
  readonly onClaim: (publicReportRef: string) => void;
}) {
  const [ref, setRef] = useState("");
  return (
    <details className="enrein-guided-advanced-block">
      <summary>Claim report by ID</summary>
      <label className="enrein-guided-field">
        <span>Public report ref</span>
        <input
          className="enrein-guided-scan-input"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="report_…"
        />
      </label>
      <GuidedButton variant="line" onClick={() => ref.trim() && onClaim(ref.trim())}>
        Claim
      </GuidedButton>
    </details>
  );
}
